import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPathWithRef } from '../utils/attribution';
import { mvpApi } from '../services/api';
import { showPopupMessage } from '../utils/showPopup';
import LanguageSwitch from '../components/LanguageSwitch';
import './Account.css';

/**
 * Account page: Edit profile, Refer, Terms, Privacy, Settings, Delete account, Log out. Matches OG AccountPage.
 */
export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await mvpApi.deleteAccount();
      setShowDeleteModal(false);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('immigrationSessionId');
      navigate(getPathWithRef('/login'), { replace: true });
    } catch (err) {
      console.error('Delete account error:', err);
      showPopupMessage(err.response?.data?.detail || t('errorOccurred', 'Something went wrong.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('immigrationSessionId');
    navigate(getPathWithRef('/login'), { replace: true });
  };

  return (
    <div className="mvp-account-page">
      <header className="mvp-account-header">
        <button type="button" className="mvp-account-back" onClick={() => navigate('/dashboard')}>
          ← {t('back')}
        </button>
        <h1 className="mvp-account-title">{t('account.title')}</h1>
        <LanguageSwitch />
      </header>

      <main className="mvp-account-main">
        <section className="mvp-account-section" aria-label={t('account.settings', 'Account settings')}>
          <h2 className="mvp-account-section-title">{t('account.settings', 'Account settings')}</h2>
          <ul className="mvp-account-list" role="list">
            <li>
              <Link to="/profile" className="mvp-account-list-item">
                <span className="mvp-account-list-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <span className="mvp-account-list-label">{t('account.editProfile', 'Edit profile')}</span>
                <span className="mvp-account-list-chevron">›</span>
              </Link>
            </li>
            <li>
              <Link to="/refer" className="mvp-account-list-item">
                <span className="mvp-account-list-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </span>
                <span className="mvp-account-list-label">{t('account.referFriends')}</span>
                <span className="mvp-account-list-chevron">›</span>
              </Link>
            </li>
            <li>
              <Link to="/settings" className="mvp-account-list-item">
                <span className="mvp-account-list-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </span>
                <span className="mvp-account-list-label">{t('settings.title', 'Settings')}</span>
                <span className="mvp-account-list-chevron">›</span>
              </Link>
            </li>
          </ul>
        </section>

        <section className="mvp-account-section" aria-label={t('legal.title', 'Legal')}>
          <h2 className="mvp-account-section-title">{t('legal.title', 'Legal')}</h2>
          <ul className="mvp-account-list" role="list">
            <li>
              <Link to="/terms" className="mvp-account-list-item">
                <span className="mvp-account-list-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </span>
                <span className="mvp-account-list-label">{t('legal.termsOfService', 'Terms of Service')}</span>
                <span className="mvp-account-list-chevron">›</span>
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="mvp-account-list-item">
                <span className="mvp-account-list-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <span className="mvp-account-list-label">{t('legal.privacyPolicy', 'Privacy Policy')}</span>
                <span className="mvp-account-list-chevron">›</span>
              </Link>
            </li>
          </ul>
        </section>

        <section className="mvp-account-section mvp-account-section-actions">
          <h2 className="mvp-account-section-title">{t('account.actions', 'Actions')}</h2>
          <ul className="mvp-account-list" role="list">
            <li>
              <button
                type="button"
                className="mvp-account-list-item mvp-account-list-item-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <span className="mvp-account-list-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </span>
                <span className="mvp-account-list-label">{t('account.deleteAccount', 'Delete account')}</span>
                <span className="mvp-account-list-chevron">›</span>
              </button>
            </li>
          </ul>
          <div className="mvp-account-logout-wrap">
            <button type="button" className="mvp-account-logout-btn" onClick={handleLogout}>
              {t('logOut')}
            </button>
          </div>
        </section>
      </main>

      {showDeleteModal && (
        <div
          className="mvp-account-delete-overlay"
          onClick={() => !deleting && setShowDeleteModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
        >
          <div className="mvp-account-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="delete-account-title" className="mvp-account-delete-title">
              {t('account.deleteAccountTitle', 'Delete account?')}
            </h2>
            <p className="mvp-account-delete-warning">
              {t('account.deleteAccountWarning', 'This will permanently remove your account and all your data. This cannot be undone.')}
            </p>
            <div className="mvp-account-delete-actions">
              <button
                type="button"
                className="mvp-account-delete-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                {t('dataPanel.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="mvp-account-delete-confirm"
                disabled={deleting}
                onClick={handleDeleteAccount}
              >
                {deleting ? t('loading') : t('account.deleteAccountConfirm', 'Delete my account')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
